// My Dashboard urgency helpers — due/expiry maths + the flashing "!" marks.
import { AlertTriangle, Clock } from "lucide-react";

const DAY = 86400000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

// → { level: overdue|today|soon|later|none, label, sortKey }
export function dueInfo(due, time, done) {
  if (!due || done) return { level: "none", label: due ? "" : "no date", sortKey: 9e15 };
  const d = new Date(String(due).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(+d)) return { level: "none", label: "", sortKey: 9e15 };
  const today = startOfDay(new Date());
  const days = Math.round((d - today) / DAY);
  if (days < 0) return { level: "overdue", label: `${-days}d over`, sortKey: +d };
  if (days === 0) {
    if (time && /^\d{1,2}:\d{2}/.test(time)) {
      const [h, m] = time.split(":").map(Number);
      const at = new Date(); at.setHours(h, m, 0, 0);
      const mins = Math.round((at - Date.now()) / 60000);
      if (mins < 0) return { level: "overdue", label: `${Math.ceil(-mins / 60)}h over`, sortKey: +at };
      return { level: "today", label: mins < 60 ? `in ${mins}m` : `in ${Math.round(mins / 60)}h`, sortKey: +at };
    }
    return { level: "today", label: "today", sortKey: +d };
  }
  if (days <= 3) return { level: "soon", label: `in ${days}d`, sortKey: +d };
  return { level: "later", label: `in ${days}d`, sortKey: +d };
}

export const rowUrgencyCls = (level) => (level === "overdue" ? "t2-row-overdue" : level === "today" ? "t2-row-today" : "");

export function DueBadge({ info, testid }) {
  if (!info || info.level === "none" || info.level === "later") return info?.label ? <span className="text-[10px] font-mono text-slate-500">{info.label}</span> : null;
  const cls = info.level === "overdue" ? "text-rose-300 border-rose-500/40 bg-rose-500/10"
    : info.level === "today" ? "text-amber-200 border-amber-500/40 bg-amber-500/10" : "text-sky-200 border-sky-500/30 bg-sky-500/10";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-mono font-semibold whitespace-nowrap ${cls}`} data-testid={testid}>
      {info.level === "overdue" ? <Bang /> : info.level === "today" ? <Bang tone="amber" still /> : <Clock className="w-2.5 h-2.5" />}
      {info.label}
    </span>
  );
}

// The flashing exclamation mark.
export function Bang({ tone = "rose", still = false, title }) {
  return (
    <span title={title} className={`inline-grid place-items-center w-3.5 h-3.5 rounded-full text-[9px] font-black leading-none ${still ? "" : "t2-bang"} ${tone === "amber" ? "bg-amber-400 text-[#1a1203]" : "bg-rose-500 text-white"}`}>!</span>
  );
}

export function AlertPill({ count, label = "overdue", testid }) {
  if (!count) return null;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-200 text-[10px] font-mono font-semibold" data-testid={testid}>
      <Bang /> {count} {label}
    </span>
  );
}

// Quote expiry → chip info.
export function expiryInfo(iso) {
  if (!iso) return null;
  const days = Math.round((startOfDay(new Date(iso)) - startOfDay(new Date())) / DAY);
  if (Number.isNaN(days)) return null;
  if (days < 0) return { level: "overdue", label: `expired ${-days}d` };
  if (days <= 1) return { level: "overdue", label: days === 0 ? "expires today" : "expires tmrw" };
  if (days <= 3) return { level: "today", label: `${days}d left` };
  return { level: "later", label: `${days}d left` };
}

export const daysSince = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso)) / DAY);
  return Number.isNaN(d) ? null : d;
};
export const coldCls = (days) => (days == null ? "text-slate-500" : days > 180 ? "text-rose-300" : days > 90 ? "text-orange-300" : days > 60 ? "text-amber-300" : "text-emerald-300");

export function StaleFlag({ days, after = 7, testid }) {
  if (days == null || days < after) return null;
  return <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-300" title={`No update for ${days} days`} data-testid={testid}><AlertTriangle className="w-3 h-3" />stale {days}d</span>;
}
